import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AccountsByApplicationSearchParams,
  IApplicationListItem,
} from 'src/app/shared/interfaces/account/account.interface';
import {
  IUserAccountCheckable,
  IUserAccountDTO,
} from 'src/app/shared/interfaces/user/user.interface';
import { AccountGQLService } from 'src/app/shared/services/account/account.graphql.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { UserStateService } from 'src/app/shared/services/users/user-state.service';

@Component({
  selector: 'accounts-to-user-grid',
  templateUrl: './add-accounts-to-user-grid.component.html',
})
export class AddAccountsToUserGridComponent implements OnChanges {
  @Input() userApplications?: IApplicationListItem[];
  @Input() userAccounts?: IUserAccountDTO[];
  @Input() isFromModal = false;
  @Input() isUserEdit = false;
  @Input() profileMode = false;
  @Input() searchParams: AccountsByApplicationSearchParams;
  @Input() disabled = false;

  @Output() accountsApplay = new EventEmitter<IUserAccountDTO[]>();
  @Output() accountsCheck = new EventEmitter<IUserAccountDTO[]>();

  accounts?: IUserAccountCheckable[];
  checkedAccounts?: IUserAccountCheckable[];
  accountsCopy?: IUserAccountCheckable[];

  nextPageCursor: string;
  emptyText: string;

  isAccountsSelected = false;
  isAllAccountsChacked?: boolean;
  isAccountsLoading = false;

  constructor(
    private accountGQLService: AccountGQLService,
    private modalService: ModalService,
    private userStateService: UserStateService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    const userApplications = changes.userApplications.currentValue;
    const userAccounts = changes.userAccounts.currentValue;
    const searchParams = changes.searchParams.currentValue;

    this.emptyText = this.profileMode
      ? 'Seems like some error occured.<br /> Conatct your adminstrator to add account(s) to this user.'
      : "You can add a account(s) by clicking the <i class='fas fa-plus-circle'></i> button above";

    if (userApplications) {
      if (this.isUserEdit) {
        this.isUserEdit = false;
      } else {
        this.isAccountsSelected = false;
        this.accounts = [];
        this.checkedAccounts = [];
        this.getAccountsByApplications(
          userApplications as IApplicationListItem[],
        );
      }
    }

    if (userAccounts) {
      const accs: IUserAccountCheckable[] =
        userAccounts.length > 0 ? userAccounts : [];
      if (this.isFromModal) {
        this.checkedAccounts = accs;
      } else {
        this.setAccounts(accs);
      }

      if (this.profileMode) {
        this.setAccounts(accs);
      }
    }

    if (searchParams) {
      this.accounts = [];
      this.nextPageCursor = '';
      if (this.userApplications) {
        this.getAccountsByApplications(this.userApplications);
      }
    }
  }

  toggleAllAccounts(event?) {
    if (event) {
      this.isAllAccountsChacked = event.target.checked;
    }
    const condition = this.isAllAccountsChacked ?? false;

    if (condition) {
      const tmp = this.accounts?.map((x) => {
        x.checked = this.isAllAccountsChacked ?? false;
        return x;
      });
      this.checkedAccounts = this.isAllAccountsChacked ? tmp : [];
    }

    this.accountsCheck.emit(this.checkedAccounts);
  }

  onAccountCheckboxChange(value: IUserAccountCheckable) {
    this.checkedAccounts = this.checkedAccounts?.some((x) => {
      return x.accountNumber === value.accountNumber;
    })
      ? this.checkedAccounts.filter((x) => {
          return x.accountNumber !== value.accountNumber;
        })
      : [...(this.checkedAccounts ?? []), value];

    if (this.accounts && this.checkedAccounts.length > 0) {
      this.isAllAccountsChacked =
        this.checkedAccounts.length === this.accounts.length;
    } else {
      this.isAllAccountsChacked = false;
    }

    this.accountsCheck.emit(this.checkedAccounts);
  }

  onAccountApplay() {
    this.accountsCopy = this.accounts;
    this.accounts = this.checkedAccounts;
    this.isAccountsSelected = true;

    this.accountsApplay.emit(this.checkedAccounts);
  }

  async onAccountDelete(account: IUserAccountCheckable) {
    const modalText = {
      bodyTitle: 'Are you sure you want to remove this account?',
      bodyText: 'This action will remove account from this user.',
    };

    await this.modalService.open('confirm', modalText).result;

    this.accounts = this.accounts?.filter((item) => {
      return item.accountNumber !== account.accountNumber;
    });
    this.checkedAccounts = this.accounts;

    if (this.accounts && this.accounts.length === 0) {
      this.isAccountsSelected = false;
      this.accounts = this.accountsCopy;
    }

    if (
      this.accounts &&
      this.checkedAccounts &&
      this.checkedAccounts.length > 0
    ) {
      this.isAllAccountsChacked =
        this.checkedAccounts.length === this.accounts.length;
    }

    this.accountsCheck.emit(this.checkedAccounts);
  }

  onScroll() {
    if (this.nextPageCursor && !this.isAccountsSelected) {
      if (this.userApplications) {
        this.getAccountsByApplications(this.userApplications);
      }
    }
  }

  private getAccountsByApplications(applications: IApplicationListItem[]) {
    const codes = applications.map((x) => {
      return x.code;
    });

    const allowAllAccounts =
      this.userStateService.user?.accessLevel == 'SECURE_CONNECT' ||
      this.userStateService.user?.accessLevel == 'VENDOR';

    const allowedAccountNumbers =
      allowAllAccounts ||
      (this.userStateService.user?.accounts || []).length === 0
        ? undefined
        : this.userStateService.user?.accounts?.map((x) => {
            return x.accountNumber;
          });

    this.isAccountsLoading = true;
    this.accountGQLService
      .getAccountsByApplication({
        applicationsCodes: codes,
        cursor: this.nextPageCursor,
        searchParams: this.searchParams,
        allowedAccountNumbers,
      })
      .subscribe({
        next: (res) => {
          const page = res.pageInfo;
          const accounts = res.nodes;

          this.nextPageCursor = page.hasNextPage ? page.endCursor : '';

          const formatedAccs = accounts.map((account) => {
            return {
              ...account,
              checked: this.checkedAccounts?.find((a) => {
                return a.accountNumber == account.accountNumber;
              })
                ? true
                : false,
            };
          });

          this.accounts = this.accounts ?? [];
          this.accounts.push(...formatedAccs);

          if (this.checkedAccounts && this.checkedAccounts.length > 0) {
            this.isAllAccountsChacked =
              this.checkedAccounts.length === this.accounts.length;
          }

          this.isAccountsLoading = false;
        },
        error: () => {
          this.isAccountsLoading = false;
        },
      });
  }

  private setAccounts(accounts: IUserAccountCheckable[]) {
    this.accounts = accounts;
    this.checkedAccounts = this.accounts;
    this.isAllAccountsChacked = true;
    this.isAccountsSelected = true;
  }
}
